import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Download, FileText, Loader2, Wallet, ChevronDown, ChevronUp, Banknote, TrendingDown, RefreshCw } from 'lucide-react';
import { payslipService } from '../services/payslip';
import { useLanguage } from '../contexts/LanguageContext';
import type { Payslip } from '../types';

function formatRupiah(n: number): string {
  return 'Rp ' + n.toLocaleString('id-ID');
}

function statusBadge(status: Payslip['status'], labels: Record<string, string>): React.JSX.Element {
  const variants: Record<string, 'success' | 'default' | 'outline'> = {
    paid: 'success',
    confirmed: 'default',
    closed: 'outline',
  };
  return (
    <Badge variant={variants[status] ?? 'default'} className="text-[10px] px-1.5 py-0 capitalize">
      {labels[status] ?? status}
    </Badge>
  );
}

export default function PayslipSection(): React.JSX.Element {
  const { t } = useLanguage();
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadPayslips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await payslipService.getPayslips();
      setPayslips(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.failedLoadPayslips);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPayslips();
  }, [loadPayslips]);

  const handleDownload = async (slip: Payslip): Promise<void> => {
    setDownloadingId(slip.id);
    try {
      const filename = `payslip-${slip.periodStart.slice(0, 7)}.pdf`;
      await payslipService.downloadPayslip(slip.id, filename);
    } catch {
      // Silently fail — user will notice the file didn't download
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="text-sm">{t.loadingPayslips}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 mb-4">
              <FileText className="h-7 w-7 text-destructive" />
            </div>
            <p className="text-sm text-muted-foreground mb-3">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void loadPayslips()}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              {t.retry}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (payslips.length === 0) {
    return (
      <Card className="shadow-sm">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-4">
              <Wallet className="h-7 w-7 text-muted-foreground" />
            </div>
            <h3 className="text-base font-semibold mb-1">{t.noPayslips}</h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              {t.noPayslipsDesc}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {payslips.map((slip) => {
        const isExpanded = expandedId === slip.id;
        const isDownloading = downloadingId === slip.id;
        return (
          <Card key={slip.id} className={`shadow-sm transition-all ${isExpanded ? 'ring-1 ring-primary/20' : ''}`}>
            {/* Main Row */}
            <div
              className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => setExpandedId(isExpanded ? null : slip.id)}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                    <Wallet className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-foreground truncate">{slip.periodName}</h3>
                      {statusBadge(slip.status, { paid: t.paid, confirmed: t.confirmed, closed: t.closed })}
                      {slip.isProrated && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{t.prorated}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{slip.periodStart} — {slip.periodEnd}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-bold text-foreground">{formatRupiah(slip.netSalary)}</p>
                    <p className="text-xs text-muted-foreground">{t.netSalary}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Expanded Detail */}
            {isExpanded && (
              <div className="px-4 pb-4 pt-0 border-t border-border animate-fade-in">
                {/* Net salary (mobile) */}
                <div className="sm:hidden pt-3 pb-2 text-center">
                  <p className="text-lg font-bold text-foreground">{formatRupiah(slip.netSalary)}</p>
                  <p className="text-[10px] text-muted-foreground">{t.netSalary}</p>
                </div>

                {/* Earnings */}
                <div className="pt-3 space-y-2">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Banknote className="h-3.5 w-3.5 text-success" /> {t.earnings}
                  </p>
                  <div className="bg-success/5 rounded-lg p-3 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.basicSalary}</span>
                      <span className="font-medium">{formatRupiah(slip.basicSalary)}</span>
                    </div>
                    {slip.overtimeAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t.overtimePay}</span>
                        <span className="font-medium">{formatRupiah(slip.overtimeAmount)}</span>
                      </div>
                    )}
                    {slip.allowances > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t.allowances}</span>
                        <span className="font-medium">{formatRupiah(slip.allowances)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold border-t border-success/20 pt-1.5">
                      <span>{t.grossSalary}</span>
                      <span className="text-success">{formatRupiah(slip.grossSalary)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                {slip.totalDeductions > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <TrendingDown className="h-3.5 w-3.5 text-destructive" /> {t.deductions}
                    </p>
                    <div className="bg-destructive/5 rounded-lg p-3 space-y-1.5">
                      {slip.deductions > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t.deductions}</span>
                          <span className="font-medium text-destructive">- {formatRupiah(slip.deductions)}</span>
                        </div>
                      )}
                      {slip.advancesDeducted > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t.salaryAdvanceDeduction}</span>
                          <span className="font-medium text-destructive">- {formatRupiah(slip.advancesDeducted)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-semibold border-t border-destructive/20 pt-1.5">
                        <span>{t.totalDeductions}</span>
                        <span className="text-destructive">- {formatRupiah(slip.totalDeductions)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Download Button */}
                <div className="mt-4">
                  <Button
                    className="w-full gap-2"
                    onClick={(e) => { e.stopPropagation(); void handleDownload(slip); }}
                    disabled={isDownloading}
                  >
                    {isDownloading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    {isDownloading ? t.downloading : t.downloadPayslip}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
